import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, FileSpreadsheet, Trash2, Pencil, ListChecks, PenLine, GitMerge } from "lucide-react";
import FormBuilder from "@/components/FormBuilder";
import FormFiller from "@/components/FormFiller";
import { Link } from "react-router-dom";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const CATEGORY_ORDER = ["Vehicle Checks", "Service & Installation", "Customer Pre-Visit", "Health & Safety", "Shift & Job", "Finance", "Other", "Uncategorised"];

export default function FormsPage() {
  const { can } = useAuth();
  const canManage = can("forms.manage");
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filling, setFilling] = useState(null);

  const load = () => api.get("/forms").then((r) => setForms(r.data.forms || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openEdit = async (f) => {
    try { const r = await api.get(`/forms/${f.id}`); setEditing(r.data); }
    catch { toast.error("Could not open form"); }
  };
  const del = async (f) => {
    if (!window.confirm(`Delete form "${f.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/forms/${f.id}`); toast.success("Form deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const groups = {};
  forms.forEach((f) => { const c = f.category || "Uncategorised"; (groups[c] = groups[c] || []).push(f); });
  const cats = Object.keys(groups).sort((a, b) => {
    const ia = CATEGORY_ORDER.indexOf(a), ib = CATEGORY_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
  });

  const Card = (f) => (
    <div key={f.id} className="group bg-white border border-slate-200 rounded-2xl shadow-sm p-5 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer flex flex-col"
      onClick={() => (canManage ? openEdit(f) : setFilling(f.id))} data-testid={`form-card-${f.id}`}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-base font-bold text-slate-900 leading-snug">{f.name}</h3>
        {canManage && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={(e) => { e.stopPropagation(); openEdit(f); }} className="text-slate-400 hover:text-indigo-600" title="Edit"><Pencil className="w-4 h-4" /></button>
            <button onClick={(e) => { e.stopPropagation(); del(f); }} className="text-slate-400 hover:text-rose-600" title="Delete" data-testid={`form-delete-${f.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      {f.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{f.description}</p>}
      <button onClick={(e) => { e.stopPropagation(); setFilling(f.id); }} data-testid={`form-fill-${f.id}`}
        className="mt-3 self-start inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-indigo-700 hover:bg-indigo-100 transition-colors">
        <PenLine className="w-3.5 h-3.5" /> Fill out
      </button>
      <div className="mt-auto pt-4 flex items-center gap-2 flex-wrap text-xs text-slate-400">
        <span className={`inline-flex items-center gap-1 font-medium px-2 py-0.5 rounded-full ${f.usage === "On Site / App" ? "bg-emerald-100 text-emerald-700" : f.usage === "Pre Site" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}`} data-testid={`form-usage-${f.id}`}>{f.usage || "On Site / App"}</span>
        <span className="inline-flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> {f.field_count} field{f.field_count === 1 ? "" : "s"}</span>
        <span>Updated {fmt(f.updated_at)}</span>
      </div>
    </div>
  );

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="forms-page">
      <div className="mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <PageIcon />
            <div>
              <h1 className="ted-page-title">Forms</h1>
              <p className="ted-subtle mt-1">Build reusable forms with dynamic fields and if / or conditional logic.</p>
            </div>
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
              <Link to="/forms/rule-groups" data-testid="forms-rule-groups-link">
                <Button variant="outline" className="gap-1.5"><GitMerge className="w-4 h-4" /> Rule Groups</Button>
              </Link>
              <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setEditing("new")} data-testid="form-add-btn"><Plus className="w-4 h-4" /> New Form</Button>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : forms.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
          <FileSpreadsheet className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No forms yet.{canManage ? " Click New Form to build your first one." : ""}</p>
        </div>
      ) : (
        <div className="space-y-8">
          {cats.map((cat) => (
            <section key={cat} data-testid={`form-category-${cat}`}>
              <div className="flex items-center gap-3 mb-3">
                <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">{cat}</h2>
                <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">{groups[cat].length}</span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {groups[cat].map(Card)}
              </div>
            </section>
          ))}
        </div>
      )}

      {editing && (
        <FormBuilder form={editing === "new" ? null : editing} canManage={canManage}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {filling && (
        <FormFiller formId={filling} context={{}} onClose={() => setFilling(null)} onSubmitted={() => setFilling(null)} />
      )}
    </div>
  );
}
